import React from 'react';
import { Tooltip, Avatar, AvatarGroup } from '@mui/material';
import { stringAvatar } from '../../utils/AvatarUtils';

/**
 * TaskAssigneeAvatars - Reusable component to display task assignees with avatars
 *
 * @param {Object} props
 * @param {Array} props.assignees - Array of assignees (can be objects with displayName or strings)
 * @param {Object} props.sx - Style overrides for the AvatarGroup
 * @param {number} props.max - Maximum number of avatars to show
 * @param {boolean} props.isTerminated - Whether the task is terminated
 * @param {string} props.terminatedClass - Class name to apply when terminated
 * @param {string} props.tooltipPlacement - Placement for the tooltip (default: bottom-start)
 * @param {string} props.unassignedText - Text to display when there are no assignees
 */
const TaskAssigneeAvatars = ({
  assignees,
  sx = {},
  max = 4,
  isTerminated = false,
  terminatedClass = 'terminatedAvatar',
  tooltipPlacement = 'bottom-start',
  unassignedText = 'Unassigned',
}) => {
  if (!assignees || assignees.length === 0) {
    return <span style={{ fontSize: '12px', color: '#999' }}>{unassignedText}</span>;
  }

  const assigneeNames = assignees
    .map(user => {
      if (typeof user === 'object') {
        return user.displayName || user.email || user.id || user.userId || 'Unknown';
      }
      return user;
    })
    .join(', ');

  return (
    <Tooltip
      title={assigneeNames}
      placement={tooltipPlacement}
      PopperProps={{
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 5],
            },
          },
        ],
      }}
    >
      <AvatarGroup max={max} spacing="small" sx={sx}>
        {assignees.map((user, index) => (
          <Avatar
            key={
              typeof user === 'object'
                ? user.displayName || user.id || user.userId || user.email || index
                : user
            }
            {...stringAvatar(user)}
            className={isTerminated ? terminatedClass : ''}
          />
        ))}
      </AvatarGroup>
    </Tooltip>
  );
};

export default TaskAssigneeAvatars;
